import { URL_SET_SOURCES } from '../../../../types/UrlSetSources';
import { ACTION_TYPES, PropertySettingsAction, PropertySettingsState } from '../action-types';
import { isDefined } from '../utils';

export function propertySettingsReducer(
	state: PropertySettingsState,
	action: PropertySettingsAction
): PropertySettingsState {
	const { type } = action;

	switch (type) {
		case ACTION_TYPES.INIT_FROM_SERVER: {
			const { property, sourceMode } = action.payload;
			return {
				...state,
				draft: property,
				original: property,
				sourceMode,
				isLoading: false,
				errors: []
			};
		}

		case ACTION_TYPES.INIT_NEW: {
			const { property } = action.payload;
			return {
				...state,
				draft: property,
				original: null,
				sourceMode: URL_SET_SOURCES.URL_DISCOVERY,
				isLoading: false,
				errors: []
			};
		}

		case ACTION_TYPES.SET_NAME: {
			const { payload: name } = action;
			return { ...state, draft: { ...state.draft, name } };
		}

		case ACTION_TYPES.SET_SOURCE_MODE: {
			const { payload: sourceMode } = action;
			return { ...state, sourceMode };
		}

		case ACTION_TYPES.SET_SCHEDULING: {
			const { payload: scheduledJobConfiguration } = action;
			return { ...state, draft: { ...state.draft, scheduledJobConfiguration } };
		}

		case ACTION_TYPES.SET_LOGIN: {
			const { scanLogin, urlSetLogin, cookieConsent } = action.payload;
			return {
				...state,
				draft: {
					...state.draft,
					scanConfiguration: {
						...state.draft.scanConfiguration,
						loginConfiguration: scanLogin,
						cookieConsentConfiguration: cookieConsent
					},
					urlSetConfiguration: {
						...state.draft.urlSetConfiguration,
						discoveryConfiguration: state.draft.urlSetConfiguration?.discoveryConfiguration
							? {
									...state.draft.urlSetConfiguration.discoveryConfiguration,
									loginConfiguration: urlSetLogin,
									cookieConsentConfiguration: cookieConsent
								}
							: undefined
					}
				}
			};
		}

		case ACTION_TYPES.SET_SEEDS: {
			const { urls, seeds } = action.payload;
			if (isDefined(urls)) {
				return { ...state, draft: { ...state.draft, urls } };
			}
			return {
				...state,
				draft: {
					...state.draft,
					urlSetConfiguration: {
						...state.draft.urlSetConfiguration,
						discoveryConfiguration: {
							...state.draft.urlSetConfiguration?.discoveryConfiguration,
							seeds
						}
					}
				}
			};
		}

		case ACTION_TYPES.SET_SCOPE: {
			const { languageFilter, urlPatternFilter, domainFilter } = action.payload;
			return {
				...state,
				draft: {
					...state.draft,
					urlSetConfiguration: {
						...state.draft.urlSetConfiguration,
						discoveryConfiguration: {
							...state.draft.urlSetConfiguration?.discoveryConfiguration,
							languageFilter,
							urlPatternFilter,
							domainFilter
						}
					}
				}
			};
		}

		case ACTION_TYPES.SET_GROUPING: {
			const { payload: groupingRules } = action;
			return {
				...state,
				draft: {
					...state.draft,
					urlSetConfiguration: {
						...state.draft.urlSetConfiguration,
						discoveryConfiguration: {
							...state.draft.urlSetConfiguration?.discoveryConfiguration,
							groupingRules
						}
					}
				}
			};
		}

		case ACTION_TYPES.SET_MAX_PAGES: {
			const { payload: maxPagesCount } = action;
			return {
				...state,
				draft: {
					...state.draft,
					urlSetConfiguration: {
						...state.draft.urlSetConfiguration,
						discoveryConfiguration: {
							...state.draft.urlSetConfiguration?.discoveryConfiguration,
							maxPagesCount
						}
					}
				}
			};
		}

		case ACTION_TYPES.SET_FILE_TYPE: {
			const { payload: allowedContentTypes } = action;
			return {
				...state,
				draft: {
					...state.draft,
					urlSetConfiguration: {
						...state.draft.urlSetConfiguration,
						discoveryConfiguration: {
							...state.draft.urlSetConfiguration?.discoveryConfiguration,
							allowedContentTypes
						}
					}
				}
			};
		}

		case ACTION_TYPES.SET_ERRORS: {
			const { payload: errors } = action;
			return { ...state, errors };
		}

		case ACTION_TYPES.CLEAR_SECTION_ERROR: {
			const { payload: sectionId } = action;
			return { ...state, errors: state.errors.filter((e) => e.sectionId !== sectionId) };
		}

		case ACTION_TYPES.SET_LOADING: {
			const { payload: isLoading } = action;
			return { ...state, isLoading };
		}

		case ACTION_TYPES.SET_SAVING: {
			const { payload: isSaving } = action;
			return { ...state, isSaving };
		}

		case ACTION_TYPES.MARK_SAVED:
			return {
				...state,
				original: state.draft,
				isSaving: false,
				isLoading: false
			};

		case ACTION_TYPES.RESET_DRAFT:
			return {
				...state,
				draft: state.original || state.draft,
				errors: []
			};

		case ACTION_TYPES.APPLY_SOURCE_CONVERSION: {
			const { payload: draft } = action;
			return {
				...state,
				draft,
				sourceMode: draft.urlSetConfiguration?.source ?? state.sourceMode,
				isLoading: false
			};
		}

		case ACTION_TYPES.SET_DRAFT: {
			const { payload: draft } = action;
			return draft === state.draft ? state : { ...state, draft };
		}

		default:
			return state;
	}
}
